import { useEffect, useState } from "react";
import { NavLink, Routes, Route, Link, useParams } from "react-router-dom";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, errCode, evidenceUrl } from "@/lib/api";
import StatusBadge from "@/components/store/StatusBadge";
import { OrderView } from "@/pages/Track";
import { InvoiceButton } from "@/components/store/Invoice";
import SecurityPanel from "@/components/SecurityPanel";
import { LOGOUT } from "@/constants/testIds";

const CATS = ["change_of_mind", "not_working", "manufacturing_defect", "damaged_in_shipping", "wrong_item", "missing_part", "other_issue"];

function Orders() {
  const { t, money, date } = useI18n();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/account/orders").then((r) => setRows(r.data)); }, []);
  if (!rows) return null;
  if (!rows.length) return <div className="card p-10 text-center muted space-y-4"><p>{t("account.no_orders")}</p><Link to="/products" className="btn-dark">{t("cart.browse")}</Link></div>;
  return <div className="space-y-3">{rows.map((o) => (
    <Link key={o.order_number} to={`/account/orders/${o.order_number}`} data-testid={`account-order-${o.order_number}`} className="card p-5 flex items-center justify-between gap-3 hover:border-[#2563EB] transition-colors">
      <div><p className="font-display font-bold">{o.order_number}</p><p className="text-xs muted">{date(o.created_at)} · {o.items.length} {t("account.items")}</p></div>
      <div className="text-end space-y-1"><StatusBadge status={o.status} group="order_status" /><p className="text-sm font-semibold">{money(o.total)}</p></div>
    </Link>))}</div>;
}

export function EvidenceUpload({ value, onChange }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const files = [...e.target.files].slice(0, 8 - value.length);
    setBusy(true);
    try {
      const out = [];
      for (const f of files) { const fd = new FormData(); fd.append("file", f); out.push((await api.post("/account/evidence", fd)).data); }
      onChange([...value, ...out]);
    } catch (e2) { toast.error(t(`errors.${errCode(e2)}`)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">{value.map((ev, i) => (
        <div key={ev.path} className="relative w-20 h-20 rounded-xl overflow-hidden bg-[#F3F4F6]">
          {ev.type === "image" ? <img src={evidenceUrl(ev.path)} alt="" className="w-full h-full object-cover" /> : <video src={evidenceUrl(ev.path)} className="w-full h-full object-cover" />}
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute top-1 end-1 bg-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
        </div>))}
        <label className="w-20 h-20 rounded-xl border-2 border-dashed border-[#D1D5DB] flex flex-col items-center justify-center text-xs muted cursor-pointer hover:border-[#2563EB]">
          <Upload className="w-5 h-5" />{busy ? "…" : t("returns.add_media")}
          <input data-testid="evidence-input" type="file" accept="image/jpeg,image/png,image/webp,video/mp4" multiple className="hidden" onChange={up} disabled={busy} />
        </label>
      </div>
      <p className="text-xs muted">{t("returns.media_hint")}</p>
    </div>
  );
}

function ReturnForm({ o, onDone }) {
  const { t, pick } = useI18n();
  const [f, setF] = useState({ category: "", product_id: o.items[0].product_id, qty: 1, description: "", extra_info: "", evidence: [] });
  const defect = f.category && f.category !== "change_of_mind";
  const submit = async (e) => {
    e.preventDefault();
    const it = o.items.find((i) => i.product_id === f.product_id);
    try {
      await api.post("/account/returns", { order_number: o.order_number, category: f.category, items: [{ product_id: f.product_id, sku: it.sku, qty: Number(f.qty) }],
        description: f.description, extra_info: f.extra_info, evidence: f.evidence.map(({ path, type }) => ({ path, type })) });
      toast.success(t("returns.submitted")); onDone();
    } catch (e2) { toast.error(t(`errors.${errCode(e2)}`)); }
  };
  return (
    <form onSubmit={submit} className="card p-5 sm:p-6 space-y-4" data-testid="return-form">
      <h3 className="font-semibold text-lg">{t("returns.request")}</h3>
      <label className="block"><span className="label">{t("returns.reason")}</span>
        <select data-testid="return-category-select" required className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
          <option value="">{t("returns.choose_reason")}</option>
          <optgroup label={t("returns.group_mind")}><option value="change_of_mind">{t("return_category.change_of_mind")}</option></optgroup>
          <optgroup label={t("returns.group_defect")}>{CATS.slice(1).map((c) => <option key={c} value={c}>{t(`return_category.${c}`)}</option>)}</optgroup>
        </select></label>
      {defect && <p className="text-xs rounded-lg bg-[#2563EB]/5 text-[#1D4ED8] p-3">{t("returns.defect_note")}</p>}
      <div className="grid grid-cols-[1fr_100px] gap-3">
        <label><span className="label">{t("returns.product")}</span><select data-testid="return-product-select" className="input" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })}>{o.items.map((i) => <option key={i.product_id} value={i.product_id}>{pick(i.name)}</option>)}</select></label>
        <label><span className="label">{t("returns.qty")}</span><input data-testid="return-qty-input" type="number" min={1} max={o.items.find((i) => i.product_id === f.product_id)?.qty} className="input" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} /></label>
      </div>
      <label className="block"><span className="label">{t("returns.description")}</span><textarea data-testid="return-description-input" required minLength={5} className="textarea" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={t("returns.description_ph")} /></label>
      <div><span className="label">{t("returns.media")}</span><EvidenceUpload value={f.evidence} onChange={(ev) => setF({ ...f, evidence: ev })} /></div>
      <label className="block"><span className="label">{t("returns.extra")}</span><input data-testid="return-extra-input" className="input" value={f.extra_info} onChange={(e) => setF({ ...f, extra_info: e.target.value })} /></label>
      <button data-testid="return-submit-btn" className="btn-primary w-full">{t("returns.submit")}</button>
    </form>
  );
}

function OrderDetail() {
  const { number } = useParams();
  const { t } = useI18n();
  const [o, setO] = useState(null);
  const [form, setForm] = useState(false);
  const load = () => api.get(`/account/orders/${number}`).then((r) => setO(r.data));
  useEffect(() => { load(); }, [number]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!o) return null;
  return (
    <div className="space-y-5">
      <OrderView o={o} />
      {o.payment_status === "confirmed" && <InvoiceButton load={() => api.get(`/account/orders/${number}/invoice`).then((r) => r.data)} />}
      {o.returns?.length > 0 && <ReturnsList rows={o.returns} />}
      {["shipped", "delivered"].includes(o.status) && !form && <button data-testid="open-return-form-btn" onClick={() => setForm(true)} className="btn-ghost w-full">{t("returns.request")}</button>}
      {form && <ReturnForm o={o} onDone={() => { setForm(false); load(); }} />}
    </div>
  );
}

function ReturnsList({ rows }) {
  const { t, date } = useI18n();
  const [msg, setMsg] = useState({});
  if (!rows.length) return <div className="card p-10 text-center muted">{t("returns.none")}</div>;
  return <div className="space-y-3">{rows.map((r) => (
    <div key={r.return_id} data-testid={`return-${r.rma_number}`} className="card p-5 space-y-3">
      <div className="flex justify-between gap-3 flex-wrap"><div><p className="font-display font-bold">{r.rma_number}</p><p className="text-xs muted">{r.order_number} · {date(r.created_at)} · {t(`return_category.${r.category}`)}</p></div><StatusBadge status={r.status} group="return_status" /></div>
      {r.history?.filter((h) => h.message).slice(-1).map((h) => <p key={h.at} className="text-sm rounded-lg bg-[#F5F7FA] p-3">{h.message}</p>)}
      {r.status === "info_required" && <div className="flex gap-2"><input className="input h-11" value={msg[r.return_id] || ""} onChange={(e) => setMsg({ ...msg, [r.return_id]: e.target.value })} placeholder={t("returns.reply_ph")} />
        <button onClick={() => api.post(`/account/returns/${r.return_id}/info`, { message: msg[r.return_id] }).then(() => window.location.reload())} className="btn-dark h-11">{t("common.send")}</button></div>}
    </div>))}</div>;
}

function Returns() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/account/returns").then((r) => setRows(r.data)); }, []);
  return rows && <ReturnsList rows={rows} />;
}

function Warranty() {
  const { t, pick, date } = useI18n();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/account/warranty").then((r) => setRows(r.data)); }, []);
  if (!rows) return null;
  if (!rows.length) return <div className="card p-10 text-center muted">{t("warranty.none")}</div>;
  return <div className="space-y-3">{rows.map((w) => (
    <div key={w.order_number + w.product_id} data-testid={`warranty-${w.product_id}`} className="card p-5 space-y-2">
      <div className="flex justify-between gap-3"><p className="font-medium">{pick(w.name)}</p><StatusBadge status={w.status} group="warranty_status" /></div>
      <p className="text-xs muted">{w.order_number} · {w.warranty_months ? t("product.warranty_months", { n: w.warranty_months }) : t("warranty.no_coverage")}</p>
      {w.start && <p className="text-sm">{t("warranty.from")} {date(w.start)} {w.end && <>— {t("warranty.to")} {date(w.end)}</>}</p>}
      {w.claims.length > 0 && <p className="text-xs muted">{t("warranty.claims")}: {w.claims.map((c) => `${c.rma_number} (${t(`return_status.${c.status}`)})`).join("، ")}</p>}
    </div>))}</div>;
}

function Notifications() {
  const { t, date } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/account/notifications").then((r) => setRows(r.data)); }, []);
  if (!rows.length) return <div className="card p-10 text-center muted">{t("account.no_notifications")}</div>;
  return <div className="card divide-y divide-[#F3F4F6]">{rows.map((n) => <div key={n.notification_id} className="p-4 text-sm flex justify-between gap-3"><span>{t(`notify.${n.kind}`, { order: n.order_number, tracking: n.params?.tracking_number || "" })}</span><span className="text-xs muted shrink-0">{date(n.created_at)}</span></div>)}</div>;
}

export default function Account() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const tabs = [["/account", "orders", true], ["/account/returns", "returns"], ["/account/warranty", "warranty"], ["/account/notifications", "notifications"], ["/account/security", "security"]];
  return (
    <div className="container-x py-8 sm:py-12 space-y-6" data-testid="account-page">
      <div className="flex items-center justify-between gap-3"><div><h1 className="text-3xl font-bold">{t("account.hello", { name: user.name.split(" ")[0] })}</h1><p className="text-sm muted">{user.email}</p></div>
        <button data-testid={LOGOUT.button} onClick={logout} className="btn-ghost btn-sm">{t("auth.logout")}</button></div>
      <nav className="flex gap-2 overflow-x-auto scrollbar-none">{tabs.map(([to, k, end]) => (
        <NavLink key={k} to={to} end={end} data-testid={`account-tab-${k}`} className={({ isActive }) => `btn-sm btn shrink-0 ${isActive ? "bg-[#0B0D0F] text-white" : "bg-white border border-[#E5E7EB]"}`}>{t(`account.tab_${k}`)}</NavLink>))}</nav>
      <Routes>
        <Route index element={<Orders />} />
        <Route path="orders/:number" element={<OrderDetail />} />
        <Route path="returns" element={<Returns />} />
        <Route path="warranty" element={<Warranty />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="security" element={<SecurityPanel />} />
      </Routes>
    </div>
  );
}
